import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';
import { useTheme } from '../lib/theme';
import { Button } from '../components/ui/button';
import { Shield, ArrowLeft, Sun, Moon } from 'lucide-react';
import api from '../lib/api';

export default function ForgotPassword() {
  const { t, language, changeLanguage } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#050505] text-slate-900 dark:text-white flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-purple-600 p-12 flex-col justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-white" strokeWidth={1.5} />
          <span className="text-white font-heading text-xl tracking-tight">SheetArmor</span>
        </div>
        <div>
          <h2 className="font-heading text-4xl text-white mb-4">{t('auth.left_panel_title')}</h2>
          <p className="text-white/80 text-lg font-body leading-relaxed">{t('auth.left_panel_desc')}</p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <Link to="/login" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-sm font-body">Back to Login</span>
            </Link>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white h-8 w-8 p-0"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
              </Button>
              <button
                onClick={() => changeLanguage(language === 'en' ? 'fr' : 'en')}
                className="text-xs font-body text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white transition-colors"
              >
                {language === 'en' ? 'FR' : 'EN'}
              </button>
            </div>
          </div>

          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Shield className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
            <span className="font-heading text-xl tracking-tight">SheetArmor</span>
          </div>

          {!submitted ? (
            <>
              <h1 className="font-heading text-3xl tracking-tight mb-2">
                {language === 'fr' ? 'Mot de passe oublié' : 'Forgot Password'}
              </h1>
              <p className="text-slate-600 dark:text-neutral-400 text-sm font-body mb-8">
                {language === 'fr'
                  ? 'Entrez votre adresse email pour recevoir un lien de réinitialisation.'
                  : 'Enter your email address to receive a reset link.'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-600/10 border border-red-600/30 rounded-sm p-3 text-red-600 text-sm">
                    {error}
                  </div>
                )}

                <div>
                  <label className="text-xs font-body text-slate-600 dark:text-neutral-400 uppercase tracking-wider mb-2 block">
                    {t('auth.email')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-neutral-600 focus:outline-none focus:ring-2 focus:ring-blue-600/50 font-body text-sm"
                    placeholder="name@company.com"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-black dark:hover:bg-neutral-200 h-11 text-xs font-body tracking-widest rounded-sm"
                >
                  {loading
                    ? (language === 'fr' ? 'ENVOI EN COURS...' : 'SENDING...')
                    : (language === 'fr' ? 'ENVOYER LE LIEN' : 'SEND RESET LINK')}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600/10 border border-green-600/30 rounded-sm flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-green-600" strokeWidth={1.5} />
              </div>
              <h2 className="font-heading text-2xl tracking-tight mb-2">
                {language === 'fr' ? 'Email envoyé' : 'Email Sent'}
              </h2>
              <p className="text-slate-600 dark:text-neutral-400 text-sm font-body mb-6">
                {language === 'fr'
                  ? 'Si un compte existe avec cette adresse email, vous recevrez un lien de réinitialisation dans quelques instants.'
                  : 'If an account exists with this email, you will receive a reset link shortly.'}
              </p>
              <Link to="/login">
                <Button className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-black dark:hover:bg-neutral-200 h-11 px-8 text-xs font-body tracking-widest rounded-sm">
                  {language === 'fr' ? 'RETOUR À LA CONNEXION' : 'BACK TO LOGIN'}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
